import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "react-router";
import type { Widget, WidgetTemplateListItem } from "../types";
import type { UserRole } from "@/modules/membership";
import IconRecycleBinThrow from "@/components/icons/icon-recycle-bin-throw";
import IconCheck from "@/components/icons/icon-check";
import IconCodeMonitor from "@/components/icons/icon-code-monitor";
import Tooltip from "@/components/ui/tooltip";

export interface WidgetListItemProps {
	widget: Widget;
	templates?: WidgetTemplateListItem[] | null;
	onDelete?: (id: string) => void;
	onEdit?: (id: string) => void;
	onStatusChange?: (id: string, status: string) => void;
	userRole?: UserRole | null;
}

function formatDate(dateString: string) {
	const date = new Date(dateString);
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function StatusDropdown({ status, onStatusChange, widgetId }: { status: string; onStatusChange?: (id: string, status: string) => void; widgetId: string }) {
	const isPublished = status === "published";
	const isUnpublished = status === "unpublished";

	const getStatusColor = () => (isPublished ? "text-green-700" : "text-gray-500");
	const getStatusBgColor = () => (isPublished ? "bg-green-500" : "bg-gray-400");
	const getStatusLabel = () => (isPublished ? "Published" : "Unpublished");

	if (!onStatusChange) {
		return (
			<span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${getStatusColor()}`}>
				<span className={`w-1.5 h-1.5 rounded-full ${getStatusBgColor()}`} />
				{getStatusLabel()}
			</span>
		);
	}

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<button type="button" className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md cursor-pointer transition-colors hover:bg-gray-100 ${getStatusColor()}`}>
					<span className={`w-1.5 h-1.5 rounded-full ${getStatusBgColor()}`} />
					{getStatusLabel()}
					<svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[120px]" sideOffset={5}>
					<DropdownMenu.Item className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50" onSelect={() => onStatusChange(widgetId, "published")}>
						<span className="text-gray-700">Published</span>
						{isPublished && <IconCheck className="w-4 h-4 text-brand" />}
					</DropdownMenu.Item>
					<DropdownMenu.Item className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer outline-none hover:bg-gray-50" onSelect={() => onStatusChange(widgetId, "unpublished")}>
						<span className="text-gray-700">Unpublished</span>
						{isUnpublished && <IconCheck className="w-4 h-4 text-brand" />}
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}

export default function WidgetListItem({ widget, templates, onDelete, onEdit, onStatusChange, userRole }: WidgetListItemProps) {
	const { id, status, created_at, title, display_type, template, template_version, blocked_by_plan } = widget;
	const templateLabel = templates?.find((t) => t.template === template && t.version === template_version)?.label;
	const [isHovered, setIsHovered] = useState(false);
	const navigate = useNavigate();
	const isAdmin = userRole === "admin";

	const displayTitle = title || "Untitled Widget";
	const getTypeLabel = () => {
		switch (display_type) {
			case "single":
				return "Single";
			case "collection":
				return "Collection";
			case "aggregation":
				return "Aggregation";
			default:
				return "Widget";
		}
	};
	const getTypeColor = () => {
		switch (display_type) {
			case "single":
				return "text-purple-700 bg-purple-50";
			case "collection":
				return "text-blue-700 bg-blue-50";
			case "aggregation":
				return "text-emerald-700 bg-emerald-50";
			default:
				return "text-gray-700 bg-gray-100";
		}
	};

	return (
		<div
			className={`flex items-center justify-between p-4 rounded-xl border transition-all ${blocked_by_plan ? "bg-gray-50 border-gray-200" : "bg-white border-gray-200 hover:shadow-sm hover:border-gray-300"}`}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div className={`flex items-center gap-4 min-w-0 flex-1 ${blocked_by_plan ? "opacity-75" : ""}`}>
				<div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isHovered ? "bg-brand/15" : "bg-brand/10"}`}>
					<IconCodeMonitor className="w-5 h-5 text-brand" />
				</div>

				<div className="min-w-0">
					<h3 className="text-sm font-medium text-primary truncate">
						{displayTitle}
						{templateLabel && <span className="ml-2 text-xs text-tertiary font-normal">({templateLabel})</span>}
					</h3>
					<div className="flex items-center gap-3 mt-1">
						<span className="text-xs text-tertiary">Created {formatDate(created_at)}</span>
						<span className="text-xs text-tertiary">•</span>
						<span className={`inline-flex items-center justify-center w-20 py-0.5 text-xs font-medium rounded ${getTypeColor()}`}>{getTypeLabel()}</span>
					</div>
				</div>
			</div>

			<div className="flex items-center gap-1">
			{blocked_by_plan ? (
				<>
					<span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-gray-400">
						<span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
						Disabled
					</span>

					<div className="w-px h-5 bg-gray-200 mx-1" />

					{isAdmin ? (
						<Tooltip content="This widget is not available for your current plan. Click to upgrade.">
							<button
								type="button"
								onClick={() => navigate(`/${window.location.pathname.split("/")[1]}/billing`)}
								className="p-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
							>
								<svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
								</svg>
							</button>
						</Tooltip>
					) : (
						<Tooltip content="This widget is not available for your current plan. Please upgrade to enable it.">
							<span className="p-1">
								<svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
								</svg>
							</span>
						</Tooltip>
					)}

					<div className="w-px h-5 bg-gray-200 mx-1" />
				</>
				) : (
					<>
						<StatusDropdown status={status} onStatusChange={onStatusChange} widgetId={id} />

						<div className="w-px h-5 bg-gray-200 mx-1" />

						{onEdit && (
							<button type="button" onClick={() => onEdit(id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-tertiary hover:text-primary hover:bg-gray-100 rounded-md transition-colors">
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
								</svg>
								<span>Edit</span>
							</button>
						)}
					</>
				)}

				{onDelete && (
					<button type="button" onClick={() => onDelete(id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors" aria-label="Delete widget">
						<IconRecycleBinThrow className="w-4 h-4" />
					</button>
				)}
			</div>
		</div>
	);
}
